import { openAlertModal } from 'admin/actions/alert-modal';
import { getPayspotBillboxes } from 'admin/actions/devices';
import { formatDate, toQueryParams } from 'admin/utils';
import { ListItem } from 'material-ui/List';
import ChevronRight from 'material-ui/svg-icons/navigation/chevron-right';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import Pager from '../../../../../shared/Pager';
import BillsCard from '../BillsCard';
import { PayspotDetailsCard, PayspotDetailsCardTitle } from '../PayspotDetailsCard';

const Flex = styled.div`
  display: flex;
  ${({ flexDirection }) => flexDirection && `flex-direction: ${flexDirection};`}
  ${({ justifyContent }) => justifyContent && `justify-content: ${justifyContent};`}
  ${({ alignItems }) => alignItems && `align-items: ${alignItems};`}
`;

const FlexColumn = styled.div`
  ${({ flex = 1 }) => `flex: ${flex};`}
`;

const Center = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BillboxHistoryList = styled.div`
  height: 500px;
  overflow-y: auto;
`;

const PaginationWrapper = styled.div`
  margin: 16px 0;
  ul {
    margin: 0;
  }
`;

const ItemLabel = styled.span`
  font-size: 12px;
  color: #999;
  ${({ marginRight }) => marginRight && `margin-right: ${marginRight}px;`}
`;

const ItemWrapper = styled.div``;

const ItemHeader = styled.div`
  margin-bottom: 12px;
`;

const DetailsWrapper = styled.div`
  width: 800px;
`;

const ItemSeperator = styled.span`
  width: 32px;
  color: #999;
`;

const BillboxHistoryCard = ({ payspotId }) => {
  const dispatch = useDispatch();

  const [billboxHistory, setBillboxHistory] = useState({
    rows: [],
    count: 0,
    pagination: {
      current: 1,
      has_next: false,
      has_prev: false,
      max_page: 1,
      per_page: 50,
    },
  });

  const fetchBillboxHistory = (id, pagination = {}) => {
    const params = {
      page: 1,
      per_page: 50,
      ...pagination,
    };
    getPayspotBillboxes(id, toQueryParams(params)).then(setBillboxHistory);
  };

  useEffect(() => {
    fetchBillboxHistory(payspotId);
  }, [payspotId]);

  const onViewDetails = (history) => {
    const successfulBills = JSON.parse(history.successful_bills);
    const unsuccessfulBills = JSON.parse(history.unsuccessful_bills);
    openAlertModal({
      body: (
        <DetailsWrapper>
          <Flex>
            <FlexColumn>
              <BillsCard title="金庫内訳" bills={successfulBills} />
            </FlexColumn>
            <FlexColumn>
              <BillsCard title="(参考)エラー取引内訳" bills={unsuccessfulBills} />
            </FlexColumn>
          </Flex>
        </DetailsWrapper>
      ),
      useCancel: false,
    })(dispatch);
  };

  const onChangePage = (page) => fetchBillboxHistory(payspotId, { page });

  const NoData = <span style={{ color: 'gray', fontStyle: 'italic' }}>データなし</span>;

  const makeLabledText = (label, text) => (
    <>
      <ItemLabel marginRight={8}>{label}:</ItemLabel>
      {text}
    </>
  );

  const makeLabeledDateTime = (label, datetime) => (
    <Flex flexDirection="column">
      <ItemLabel>{label}</ItemLabel>
      {datetime}
    </Flex>
  );

  const { rows, pagination } = billboxHistory;

  return (
    <PayspotDetailsCard>
      <PayspotDetailsCardTitle>金庫交換履歴</PayspotDetailsCardTitle>
      <BillboxHistoryList>
        {rows.length === 0 && <Center>{NoData}</Center>}
        {rows.map((history) => (
          <ListItem
            key={history.id}
            primaryText={
              <ItemWrapper>
                <ItemHeader>
                  <Flex>
                    <FlexColumn>{makeLabledText('ID', history.id)}</FlexColumn>
                    <ItemSeperator />
                    <FlexColumn>
                      {makeLabledText(
                        '金庫使用量',
                        <>
                          <span>{Math.round(history.used_capacity * 100)}</span>%
                        </>
                      )}
                    </FlexColumn>
                  </Flex>
                </ItemHeader>
                <Flex alignItems="center">
                  <FlexColumn>
                    {makeLabeledDateTime('設置日: ', formatDate(history.deployed_at))}
                  </FlexColumn>
                  <ItemSeperator>~</ItemSeperator>
                  <FlexColumn>
                    {makeLabeledDateTime(
                      '交換日: ',
                      history.replaced_at ? formatDate(history.replaced_at) : '投入中'
                    )}
                  </FlexColumn>
                </Flex>
              </ItemWrapper>
            }
            rightIcon={<ChevronRight />}
            onClick={() => onViewDetails(history)}
            style={{ borderBottom: '1px solid #e5e5e5' }}
          />
        ))}
      </BillboxHistoryList>
      <PaginationWrapper>
        <Pager pagination={pagination} onChangePage={onChangePage} size="sm" />
      </PaginationWrapper>
    </PayspotDetailsCard>
  );
};

BillboxHistoryCard.propTypes = {
  payspotId: PropTypes.string.isRequired,
};

export default BillboxHistoryCard;
